import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../button/button';
import type { Agent } from '../../../lib/types';
import type { ConnectedAgentStatus } from './connected-agent-status';
import { StatusPill } from './status-pill';

/** One agent from outside PromptSafe, whichever way it's connected. */
export interface ConnectedAgentRow {
  agent: Agent;
  kind: 'claude_console' | 'own_api';
  /** e.g. "bot.acme.com" under "Your API". */
  location?: string;
  /** "v3" for Claude Console, the format for an own API. */
  version: string;
  /** e.g. "Imported 24 Sep". */
  since: string | null;
  status: ConnectedAgentStatus;
}

interface ConnectedAgentsTableProps {
  rows: ConnectedAgentRow[];
  onRun: (agentId: string) => void;
  onTest: (row: ConnectedAgentRow) => Promise<void>;
  onUpdate: (agentId: string) => Promise<void>;
  onEdit: (agentId: string) => void;
  onRemove: (agentId: string) => Promise<void>;
}

const LIVES_IN = {
  claude_console: {
    mark: 'A',
    label: 'Claude Console',
    markClass: 'bg-[#1F1F1F] text-white',
  },
  own_api: {
    mark: 'API',
    label: 'Your API',
    markClass:
      'border border-[#00000020] bg-prompt-safecomwhite text-prompt-safecomabbey',
  },
};

/** Every agent that lives outside PromptSafe, with what needs attention. */
export function ConnectedAgentsTable({
  rows,
  onRun,
  onTest,
  onUpdate,
  onEdit,
  onRemove,
}: ConnectedAgentsTableProps) {
  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="connected-heading"
    >
      <div>
        <h2
          id="connected-heading"
          className="text-base font-semibold text-prompt-safecomvulcan"
        >
          Connected agents
        </h2>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-[10px] border border-dashed border-[#00000030] px-5 py-6 text-sm text-prompt-safecomabbey">
          No connected agents yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-separate border-spacing-0 overflow-hidden rounded-[10px] border border-[#00000020] bg-prompt-safecomwhite text-sm">
            <thead>
              <tr className="bg-prompt-safecomfantasy text-left text-[11px] font-semibold uppercase tracking-wider text-prompt-safecomabbey">
                <th scope="col" className="px-4 py-2.5">
                  Agent
                </th>
                <th scope="col" className="px-4 py-2.5">
                  Lives in
                </th>
                <th scope="col" className="px-4 py-2.5">
                  Version
                </th>
                <th scope="col" className="px-4 py-2.5">
                  Status
                </th>
                <th scope="col" className="px-4 py-2.5">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <AgentRow
                  key={row.agent.id}
                  row={row}
                  onRun={onRun}
                  onTest={onTest}
                  onUpdate={onUpdate}
                  onEdit={onEdit}
                  onRemove={onRemove}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AgentRow({
  row,
  onRun,
  onTest,
  onUpdate,
  onEdit,
  onRemove,
}: {
  row: ConnectedAgentRow;
} & Omit<ConnectedAgentsTableProps, 'rows'>) {
  const { agent, status } = row;
  const livesIn = LIVES_IN[row.kind];
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const act = async (action: () => Promise<void>, success: string) => {
    setIsBusy(true);
    try {
      await action();
      toast.success(success);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Try again.');
    } finally {
      setIsBusy(false);
      setIsConfirmingRemove(false);
    }
  };

  const cell = 'border-t border-[#00000014] px-4 py-3 align-middle';

  return (
    <tr>
      <td className={cell}>
        <Link
          to={`/agent-builder/${agent.id}`}
          className="block font-semibold text-prompt-safecomvulcan hover:underline"
        >
          {agent.name}
        </Link>
        {row.since && (
          <span className="text-xs text-prompt-safecomabbey">{row.since}</span>
        )}
      </td>
      <td className={cell}>
        <span className="flex items-center gap-2 whitespace-nowrap text-prompt-safecomabbey">
          <span
            aria-hidden="true"
            className={`grid h-5 min-w-5 place-items-center rounded px-1 text-[10px] font-semibold ${livesIn.markClass}`}
          >
            {livesIn.mark}
          </span>
          {livesIn.label}
        </span>
        {row.location && (
          <span className="mt-0.5 block font-mono text-xs text-prompt-safecomabbey">
            {row.location}
          </span>
        )}
      </td>
      <td className={`${cell} font-mono text-xs text-prompt-safecomabbey`}>
        {row.version}
      </td>
      <td className={cell}>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        {status.detail && (
          <span className="mt-1 block max-w-[32ch] text-xs text-prompt-safecomabbey">
            {status.detail}
          </span>
        )}
      </td>
      <td className={`${cell} text-right`}>
        {isConfirmingRemove ? (
          <span className="inline-flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs text-prompt-safecomabbey">
              Remove from PromptSafe?
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmingRemove(false)}
            >
              Keep
            </Button>
            <Button
              size="sm"
              disabled={isBusy}
              className="bg-red-700 text-white hover:bg-red-800"
              onClick={() =>
                act(() => onRemove(agent.id), `${agent.name} removed`)
              }
            >
              Remove
            </Button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 whitespace-nowrap">
            {status.newerVersion ? (
              <Button
                variant="outline"
                size="sm"
                disabled={isBusy}
                onClick={() =>
                  act(
                    () => onUpdate(agent.id),
                    `${agent.name} now uses v${status.newerVersion}`,
                  )
                }
              >
                Update to v{status.newerVersion}
              </Button>
            ) : status.needsTest ? (
              <Button
                variant="outline"
                size="sm"
                disabled={isBusy}
                onClick={() =>
                  act(() => onTest(row), `${agent.name} replied. It works.`)
                }
              >
                {isBusy ? 'Testing…' : 'Test'}
              </Button>
            ) : status.canRun ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRun(agent.id)}
              >
                Run a simulation
              </Button>
            ) : null}
            {row.kind === 'own_api' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(agent.id)}
              >
                Edit
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-red-700 hover:bg-red-50 hover:text-red-800"
              onClick={() => setIsConfirmingRemove(true)}
            >
              Remove
            </Button>
          </span>
        )}
      </td>
    </tr>
  );
}
